/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { inputClass, selectClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import { GET_USER_PROFILE, UPDATE_USER_PROFILE } from "api/dataProviderTypes";
import { UserRequest } from "api/types";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { getUserProfile, updateUser } from "services/users";
import { IReturnType } from "types";
import { adaptResponseError } from "utils/utils";
import { getLoggedInUserId, getUserData } from "../../utils/storageUtils";
import { hasAnyRole, roles } from "../../constants/roles";
import {
  BirthDateValue,
  EMPTY_BIRTH_DATE_VALUE,
  toBirthDateValue,
  validateBirthDateValue,
  withBirthDatePayload,
} from "../../utils/age";
import DoctorAvailabilityPanel from "../Availability/DoctorAvailabilityPanel";
import DashboardWrapper from "../Layout/DashboardWrapper";
import Alert from "../Others/Alert";
import BirthDateField from "../Others/BirthDateField";
import { toast } from "sonner";

const initialsOf = (firstName?: string, lastName?: string) =>
  ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase() || null;

// Seuls le médecin et l'administrateur renseignent des qualifications.
const QUALIFICATION_ROLES = [roles.ROLE_DOCTOR, roles.ROLE_ADMIN];

const Profile = () => {
  const userData = getUserData();
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) =>
    setForm((form) => ({
      ...form,
      [e.target.name]: e.target.value,
    }));

  const {
    data: resultData,
    isLoading,
    isFetching,
    refetch,
  }: IReturnType<UserRequest> = useQuery(
    GET_USER_PROFILE,
    () => getUserProfile(),
    { staleTime: Infinity }
  );
  const { data: profile, errors } = resultData || { data: {} };

  const { mutate, isLoading: isUpdating } = useMutation(
    UPDATE_USER_PROFILE,
    () => updateUser(withBirthDatePayload(form, birthDate)),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    const birthDateError = validateBirthDateValue(birthDate);
    if (birthDateError) {
      toast.error(birthDateError);
      return;
    }
    mutate();
  };

  const [form, setForm] = useState<UserRequest>({ ...profile });
  // Date de naissance / âge approximatif : édités à part de `form`, car seul
  // l'un des deux champs part au serveur (cf. `withBirthDatePayload`).
  const [birthDate, setBirthDate] = useState<BirthDateValue>(
    EMPTY_BIRTH_DATE_VALUE
  );

  useEffect(() => {
    setForm({ ...userData.user });
    setBirthDate(toBirthDateValue(userData.user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      setForm((prevForm) => ({ ...prevForm, ...profile }));
      setBirthDate(toBirthDateValue(profile));
    }
  }, [profile]);

  // Autorités effectives (rôle principal ∪ rôles additionnels) : on
  // interroge le profil chargé côté back et, en repli, la session en
  // localStorage qui est disponible avant la fin de la requête.
  const isDoctorOrAdmin =
    hasAnyRole(profile, QUALIFICATION_ROLES) || hasAnyRole(userData, QUALIFICATION_ROLES);

  const doctorId: number | null =
    hasAnyRole(profile, [roles.ROLE_DOCTOR]) || hasAnyRole(userData, [roles.ROLE_DOCTOR])
      ? getLoggedInUserId()
      : null;

  const initials = initialsOf(form?.firstName, form?.lastName);
  const flatErrors = errors ? adaptResponseError(errors) : [];

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching || isUpdating}>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
        {/* Hero */}
        <header className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <span className="flex h-16 w-16 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white text-xl sm:text-2xl font-semibold ring-4 ring-white shadow-lg">
              {initials || <i className="bx bx-user" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Mon compte
              </p>
              <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 truncate">
                {form?.firstName} {form?.lastName}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {form?.email}
                {form?.phoneNumber && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    {form.phoneNumber}
                  </>
                )}
              </p>
            </div>
          </div>
        </header>

        {/* Errors */}
        {flatErrors.length > 0 && (
          <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            {flatErrors.map((error: any, idx: number) => (
              <Alert key={idx} message={error.message} />
            ))}
          </div>
        )}

        {/* Section : Basics */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-1">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Coordonnées
              </p>
              <h3 className="mt-1 text-base font-semibold text-slate-800">
                Comment vous joindre
              </h3>
              <p className="mt-1 text-xs text-slate-500 leading-5">
                Une adresse e-mail à jour est essentielle pour la sécurité de
                votre compte et la récupération en cas d'oubli.
              </p>
            </div>
            <div className="xl:col-span-2 space-y-5">
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Email
                </span>
                <div className="relative">
                  <input
                    type="text"
                    name="email"
                    value={form?.email || ""}
                    onChange={handleChange}
                    className={inputClass + " pr-10"}
                  />
                  <i className="bx bx-envelope absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                </div>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Téléphone
                  </span>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={form?.phoneNumber || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Adresse
                  </span>
                  <input
                    type="text"
                    name="address"
                    value={form?.address || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Section : Profil */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-1">
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Profil
              </p>
              <h3 className="mt-1 text-base font-semibold text-slate-800">
                Informations publiques
              </h3>
              <p className="mt-1 text-xs text-slate-500 leading-5">
                Ces informations sont visibles dans l'application.
                {isDoctorOrAdmin && " Vos qualifications apparaissent sur la fiche docteur."}
              </p>
            </div>
            <div className="xl:col-span-2 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Prénom
                  </span>
                  <input
                    type="text"
                    name="firstName"
                    value={form?.firstName || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Nom
                  </span>
                  <input
                    type="text"
                    name="lastName"
                    value={form?.lastName || ""}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Nom d'utilisateur
                </span>
                <input
                  type="text"
                  name="userName"
                  value={form?.userName || ""}
                  onChange={handleChange}
                  className={inputClass}
                />
              </label>
              <BirthDateField
                value={birthDate}
                onChange={setBirthDate}
                estimated={Boolean(profile?.dateOfBirthEstimated)}
                labelClassName="block text-sm font-medium text-slate-700"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Sexe
                  </span>
                  <div className="relative">
                    <select
                      value={form?.sex || ""}
                      onChange={handleChange}
                      name="sex"
                      className={selectClass}
                    >
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                    <i className="bx bx-chevron-down absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
                  </div>
                </label>
              </div>
              {isDoctorOrAdmin && (
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700">
                    Qualifications professionnelles{" "}
                    <span className="text-xs font-normal text-slate-400">
                      (HTML autorisé)
                    </span>
                  </span>
                  <textarea
                    name="doctorQualifications"
                    value={form?.doctorQualifications || ""}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Ex. <strong>Diplôme en Ophtalmologie</strong><br/>Spécialisation en Chirurgie Oculaire"
                    className={cn(inputClass, "h-auto py-3 resize-y")}
                  />
                  <p className="mt-1.5 text-xs text-slate-400">
                    Le HTML est utilisé pour le formatage public (titres,
                    sauts de ligne, etc.).
                  </p>
                </label>
              )}
            </div>
          </div>
        </section>

        {/* CTA Save */}
        <div className="flex items-center justify-end pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-500 fill-mode-both">
          <button
            type="submit"
            onClick={handleSubmit}
            className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium px-5 py-3 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
            />
            <i className="bx bx-check text-lg" />
            Enregistrer les changements
          </button>
        </div>

        {/* Disponibilité : enregistrée à chaque modification, indépendante du formulaire ci-dessus. */}
        {doctorId != null && (
          <section
            aria-labelledby="my-availability-title"
            className="space-y-4 border-t border-slate-200 pt-8 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-500 fill-mode-both"
          >
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">Agenda</p>
              <h2 id="my-availability-title" className="mt-1 text-xl font-semibold text-slate-900">
                Mes disponibilités
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Jours et heures de consultation, et absences. Chaque modification est enregistrée immédiatement,
                sans le bouton « Enregistrer les changements ».
              </p>
            </div>
            <DoctorAvailabilityPanel doctorId={doctorId} editable />
          </section>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default Profile;
